import { isMobileApp } from '@/lib/mobileAppPlatform';
import { isCorporateWebRole } from '@/lib/corporate-web-policy.mjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export function ProfileTestBanner() {
  const { user, stopProfileTest } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  if (!user?.profile_test) return null;
  const back = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { await stopProfileTest(); navigate('/profiles', { replace: true }); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || 'No pudimos volver al administrador.'); }
    finally { setBusy(false); }
  };
  return <aside role="status" data-testid="profile-test-banner"
    className="fixed inset-x-3 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[100] mx-auto max-w-xl rounded-2xl border border-amber-400/50 bg-[#111318] p-3 text-white shadow-2xl">
    <div className="flex items-center gap-3">
      <ShieldCheck className="shrink-0 text-amber-400" size={21} />
      <div className="min-w-0 flex-1 text-xs"><strong className="block text-amber-300">Modo de prueba · {user.role_label || user.role}</strong><span className="truncate block">{user.name} · Administrador: {user.profile_test.actor_name}</span></div>
      <button type="button" onClick={back} disabled={busy} data-testid="exit-profile-test"
        className="min-h-11 shrink-0 rounded-xl border border-amber-400/50 px-3 text-sm font-semibold disabled:opacity-50">{busy ? 'Volviendo…' : 'Salir'}</button>
    </div>
    {error && <p role="alert" className="mt-2 text-xs text-red-300">{error}</p>}
  </aside>;
}

export default function ProfileTestSelector() {
  const { startProfileTest } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    api.get('/auth/test-profiles').then(({ data }) => { if (alive) { setCatalog(data); setError(''); } })
      .catch(() => { if (alive) setError('No pudimos cargar los perfiles disponibles.'); });
    return () => { alive = false; };
  }, []);
  const enter = async (role, accounts) => {
    const target = accounts.find(a => a.id === selected[role]) || accounts.find(a => !a.unavailable_reason);
    if (!target || busy) return;
    setBusy(true); setError('');
    try { const profile = await startProfileTest(target.id); navigate(profile.home_route, { replace: true }); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || 'No pudimos abrir ese perfil.'); }
    finally { setBusy(false); }
  };
  return <main className="min-h-[100dvh] bg-black px-4 py-8 text-white" data-testid="profile-test-selector">
    <div className="mx-auto max-w-3xl">
      <button type="button" onClick={() => navigate('/admin')} className="flex min-h-11 items-center gap-2 text-sm text-neutral-300"><ArrowLeft size={18}/> Volver a administración</button>
      <h1 className="mt-6 text-3xl font-black">Probar perfiles</h1>
      <p className="mt-3 text-sm text-neutral-300">Selecciona una cuenta existente. Entrarás con tu misma sesión de superadministrador y podrás volver desde cualquier pantalla.</p>
      <p className="mt-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-200">Las acciones que hagas en un perfil modifican datos reales de esa cuenta. Usa cuentas preparadas para pruebas.</p>
      {error && <p role="alert" className="mt-4 rounded-xl border border-red-400/40 p-3 text-red-300">{error}</p>}
      {!catalog && !error && <p className="mt-8 text-neutral-400">Cargando perfiles…</p>}
      {catalog && <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {catalog.roles.filter(role => role.key !== 'super_admin' && (!isMobileApp() || !isCorporateWebRole(role.key))).map(role => {
          const accounts = catalog.accounts.filter(account => account.role === role.key);
          const ready = accounts.filter(account => !account.unavailable_reason);
          const chosen = selected[role.key] || ready[0]?.id || '';
          return <section key={role.key} className="rounded-2xl border border-white/10 bg-[#111318] p-4">
            <h2 className="text-lg font-bold">{role.label}</h2>
            {accounts.length ? <>
              <label htmlFor={`profile-${role.key}`} className="mt-3 block text-xs text-neutral-400">Cuenta que deseas probar</label>
              <select id={`profile-${role.key}`} value={chosen} onChange={e => setSelected(old => ({ ...old, [role.key]: e.target.value }))}
                className="mt-1 min-h-12 w-full rounded-xl border border-white/20 bg-black p-2 text-sm text-white">
                {accounts.map(account => <option key={account.id} value={account.id} disabled={Boolean(account.unavailable_reason)} label={`${account.name || account.email} · ${account.email}${account.unavailable_reason ? ` (${account.unavailable_reason})` : ''}`} />)}
              </select>
              {!ready.length && <p className="mt-2 text-xs text-amber-300">{accounts[0].unavailable_reason}</p>}
            </> : <p className="mt-3 text-sm text-neutral-400">Aún no hay una cuenta para este perfil.</p>}
            <button type="button" disabled={!ready.length || busy} onClick={() => enter(role.key, accounts)} data-testid={`test-role-${role.key}`}
              className="mt-4 min-h-12 w-full rounded-xl bg-[#E63946] px-3 font-semibold disabled:cursor-not-allowed disabled:opacity-40">Entrar como {role.label}</button>
          </section>;
        })}
      </div>}
    </div>
  </main>;
}

